import React, { useEffect, useMemo, useRef, useState } from "react";
import { Download, LoaderCircle } from "lucide-react";
import AssessmentPDF from "@/components/assessment/AssessmentPDF";
import RobustAssessmentPdfButton from "@/components/assessment/RobustAssessmentPdfButton";
import {
  buildRobustNervousSystemProfile,
  isRobustAssessment,
} from "@/components/assessment/robustAssessmentEngine";
import {
  computeArchetype,
  computeDimensions,
  computeTodaysFocus,
} from "@/components/assessment/assessmentScoring";
import { getEvidenceMapping } from "@/components/assessment/evidenceMapping";
import { getEducationalModules } from "@/components/assessment/educationalModules";
import { buildImplementationPlan } from "@/components/assessment/implementationPlan";

const safeFilenamePart = (value, fallback) => {
  const clean = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return clean || fallback;
};

const dateFilenamePart = (value) => {
  const date = new Date(value || Date.now());
  if (Number.isNaN(date.getTime())) return "assessment";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;
};

const AdminAssessmentPdfButton = ({ assessment, participantName }) => {
  const [renderPdf, setRenderPdf] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const pdfRef = useRef(null);

  const report = useMemo(() => {
    const answers = assessment?.answers || {};
    const robust = isRobustAssessment(answers);
    const nervousSystemProfile = robust
      ? assessment?.nervousSystemProfile ||
        answers.robustProfile ||
        buildRobustNervousSystemProfile(answers)
      : null;
    const profile = computeArchetype(answers);
    const dimensions = computeDimensions(answers);
    const focus = computeTodaysFocus(answers);
    const mapping = getEvidenceMapping(profile.key);
    const plan = buildImplementationPlan({
      profile,
      mapping,
      answers,
      now: new Date(assessment?.completedAt || Date.now()),
    });
    const educationalModules = getEducationalModules({
      profileKey: profile.key,
      dimensions,
      answers,
    });
    return {
      robust,
      nervousSystemProfile,
      profile,
      dimensions,
      focus,
      mapping,
      plan,
      educationalModules,
    };
  }, [assessment]);

  useEffect(() => {
    if (report.robust || !renderPdf || !downloading) return undefined;
    let cancelled = false;

    const generate = async () => {
      try {
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        if (cancelled) return;
        const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
          import("html2canvas"),
          import("jspdf"),
        ]);
        const pdf = new jsPDF({ unit: "pt", format: "a4", compress: true });
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const node = pdfRef.current;
        const pageNodes = node
          ? Array.from(node.querySelectorAll("[data-pdf-page]"))
          : [];
        if (!pageNodes.length) {
          throw new Error("Assessment PDF template did not render.");
        }
        for (let index = 0; index < pageNodes.length; index += 1) {
          const canvas = await html2canvas(pageNodes[index], {
            scale: 2,
            useCORS: true,
            backgroundColor: "#FBF9F4",
            logging: false,
            windowWidth: 794,
          });
          if (cancelled) return;
          if (index > 0) pdf.addPage();
          pdf.addImage(
            canvas.toDataURL("image/jpeg", 0.95),
            "JPEG",
            0,
            0,
            pageWidth,
            pageHeight,
            undefined,
            "FAST",
          );
        }

        const name = safeFilenamePart(participantName, "participant");
        const date = dateFilenamePart(assessment?.completedAt);
        const resultName = safeFilenamePart(
          report.nervousSystemProfile?.headline || report.profile?.title,
          "nervous-system-profile",
        );
        pdf.save(`apos-assessment-${name}-${date}-${resultName}.pdf`);
      } catch (error) {
        console.error("Admin assessment PDF generation failed", error);
      } finally {
        if (!cancelled) {
          setDownloading(false);
          setRenderPdf(false);
        }
      }
    };

    generate();
    return () => {
      cancelled = true;
    };
  }, [
    assessment?.completedAt,
    downloading,
    participantName,
    renderPdf,
    report.nervousSystemProfile?.headline,
    report.profile?.title,
    report.robust,
  ]);

  const download = () => {
    if (downloading) return;
    setDownloading(true);
    setRenderPdf(true);
  };

  const name = safeFilenamePart(participantName, "participant");
  const date = dateFilenamePart(assessment?.completedAt);
  const resultName = safeFilenamePart(
    report.nervousSystemProfile?.headline || report.profile?.title,
    "nervous-system-profile",
  );

  if (report.robust) {
    return (
      <RobustAssessmentPdfButton
        answers={assessment?.answers || {}}
        profile={report.nervousSystemProfile}
        date={assessment?.completedAt}
        filename={`apos-assessment-${name}-${date}-${resultName}`}
        busyLabel="Building PDF..."
        className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-medium forr-btn-secondary disabled:opacity-60"
      />
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={download}
        disabled={downloading}
        className="inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-medium forr-btn-secondary disabled:opacity-60"
        title="Download this saved assessment as PDF"
      >
        {downloading ? (
          <LoaderCircle size={14} className="animate-spin" />
        ) : (
          <Download size={14} />
        )}
        {downloading ? "Building PDF…" : "Download PDF"}
      </button>

      {renderPdf && (
        <AssessmentPDF
          ref={pdfRef}
          profile={report.profile}
          focus={report.focus}
          dimensions={report.dimensions}
          mapping={report.mapping}
          plan={report.plan}
          educationalModules={report.educationalModules}
          date={assessment?.completedAt}
        />
      )}
    </>
  );
};

export default AdminAssessmentPdfButton;
